import BaseLoading from '@components/BaseLoading';
import { cameraController } from '@controllers/index';
import { CameraType } from '@EcamModel/controllers/ICameraHttpController';
import { CameraWithInfo, GeoZoneCoordinates } from '@EcamModel/model';
import { Box, Stack, Typography } from '@mui/material';
import {
    PreviewImageWithGeoZone,
    PreviewPhoto,
} from '@pages/cameras/details/components/tabsGeozonesDetail/solar/components/HistoriesPhoto';
import { getCenterFrame } from '@pages/cameras/details/utils';
import { getGeoZone, PREVIEW_RATIO } from '@pages/cameras/hook/CameraDetailsProvider';
import { useImageDimensions } from '@pages/cameras/live-photos/components/PreviewImage';
import color from '@theme/Colors';
import Konva from 'konva';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Circle, Image, Layer, Line, Stage, Text } from 'react-konva';
import { useQuery } from 'react-query';
import { formatDateByTimeZone, formatUKDateTime } from 'src/helpers';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

export default function TabCheckDeviation({ cameraId }: { cameraId: number }) {
    const stageRef = useRef<Konva.Stage>(null);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerSize, setContainerSize] = useState({
        width: window.innerWidth,
        height: window.innerWidth * PREVIEW_RATIO,
    });

    const [openPreviewPhoto, setOpenPreviewPhoto] = useState<PreviewPhoto>({
        previewLasted: false,
        previewOriginal: false,
        previewLastedEdited: false,
    });

    const getCamera = async () => {
        const res = await cameraController.get(cameraId.toString());
        return res;
    };

    const { data: camera, isLoading } = useQuery<CameraWithInfo>(['checkDeviation', cameraId], getCamera, {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
        retry: 0,
    });

    const blobLatestPhoto = camera?.CameraPhotos?.[0]?.BlobName;
    const blobOriginalPhoto = camera?.OriginalPhoto?.BlobName;
    const blobLatestEditedPhoto = camera?.LastEditedPhoto?.BlobName;

    const { image: imageLatest } = useImageDimensions(blobLatestPhoto);
    const { image: imageOriginal } = useImageDimensions(blobOriginalPhoto);
    const { image: imageLatestEdited } = useImageDimensions(blobLatestEditedPhoto);

    const scaleNotFullLatestCameraSolar = containerSize.width / (imageLatest?.width ?? 1920);
    const scaleFullLatestCameraSolar = 1280 / (imageLatest?.width ?? 1920);

    const scaleNotFullOriginalCameraSolar = containerSize.width / (imageOriginal?.width ?? 1920);
    const scaleFullOriginalCameraSolar = 1280 / (imageOriginal?.width ?? 1920);

    const scaleNotFullLatestEditedCameraSolar = containerSize.width / (imageLatestEdited?.width ?? 1920);
    const scaleFullLatestEditedCameraSolar = 1280 / (imageLatestEdited?.width ?? 1920);

    const prevGeoZonePointsCameraSolar: GeoZoneCoordinates[][] =
        (camera?.GeoZones?.map((gz) => {
            return gz.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index).concat(gz.GeoZoneCoordinates[0]);
        }) as GeoZoneCoordinates[][]) ?? [];

    const prevGeoZones = getGeoZone(scaleFullLatestCameraSolar, prevGeoZonePointsCameraSolar);
    const scaledGeoZones = getGeoZone(scaleNotFullLatestCameraSolar, prevGeoZonePointsCameraSolar);

    useEffect(() => {
        const updateStageSize = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                const height = width * PREVIEW_RATIO;
                setContainerSize({ width, height });
            } else {
                setTimeout(updateStageSize, 300);
            }
        };

        setTimeout(updateStageSize, 300);

        window.addEventListener('resize', updateStageSize);

        return () => {
            window.removeEventListener('resize', updateStageSize);
        };
    }, []);

    return isLoading ? (
        <BaseLoading displayHeight={'390px'} />
    ) : (
        <Stack p={2} spacing={2}>
            <Typography variant="body1" color={'#85858A'}>
                Latest check camera angle deviation:{' '}
                <span
                    style={{
                        fontStyle: 'italic',
                    }}
                >
                    {camera?.LastCheckAngleAt ? formatUKDateTime(camera?.LastCheckAngleAt) : 'N/A'}
                </span>
            </Typography>

            <Swiper
                style={
                    {
                        height: '390px',
                        // ['--swiper-navigation-color']: color.white,
                    } as React.CSSProperties
                }
                navigation={true}
                modules={[Navigation]}
            >
                {[
                    {
                        heading: 'Original photo',
                        captureAt: camera?.OriginalPhoto?.CaptureAt,
                        image: imageOriginal,
                        scaleNotFull: scaleNotFullOriginalCameraSolar,
                        fullScale: scaleFullOriginalCameraSolar,
                        previewKey: 'previewOriginal',
                    },
                    {
                        heading: 'Latest edited photo',
                        captureAt: camera?.LastEditedPhoto?.CaptureAt,
                        image: imageLatestEdited,
                        scaleNotFull: scaleNotFullLatestEditedCameraSolar,
                        fullScale: scaleFullLatestEditedCameraSolar,
                        previewKey: 'previewLasted',
                    },
                    {
                        heading: 'Current photo',
                        captureAt: camera?.CameraPhotos?.[0]?.CaptureAt,
                        image: imageLatest,
                        fullScale: scaleFullOriginalCameraSolar,
                        scaleNotFull: scaleNotFullLatestCameraSolar,
                        previewKey: 'previewLastedEdited',
                    },
                ].map((e, i) => {
                    const date = e?.captureAt ?? moment().toDate();
                    return (
                        <SwiperSlide
                            key={i}
                            onClick={() => {
                                setOpenPreviewPhoto((prevState) => ({
                                    ...prevState,
                                    [e.previewKey]: !prevState[e.previewKey],
                                }));
                            }}
                        >
                            <Stack width={'100%'}>
                                <Box
                                    ref={containerRef}
                                    style={{
                                        width: '100%',
                                        height: containerSize.height,
                                        margin: '10px auto 0',
                                        position: 'relative',
                                    }}
                                >
                                    <Stage
                                        ref={stageRef}
                                        width={containerSize.width}
                                        height={containerSize.height}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <Layer>
                                            <Image
                                                image={e.image}
                                                scaleX={e.scaleNotFull}
                                                scaleY={e.scaleNotFull}
                                                onClick={() => {
                                                    console.log('first');
                                                }}
                                            />
                                            {!!scaledGeoZones.length &&
                                                scaledGeoZones.map((frame, frameIndex) => {
                                                    const center = getCenterFrame(frame);
                                                    return (
                                                        <React.Fragment key={`frame-${frameIndex}`}>
                                                            <Line
                                                                points={frame.flatMap((point) => [point.x, point.y])}
                                                                stroke={color.accentPink}
                                                                fill="rgba(0, 0, 0, 0.12)"
                                                                closed={true}
                                                            />
                                                            <Circle x={center.x} y={center.y} radius={15} fill="#fff" />
                                                            <Text
                                                                x={center.x}
                                                                y={center.y}
                                                                text={`${frameIndex + 1}`}
                                                                fontSize={14}
                                                                fill="black"
                                                                align="center"
                                                                verticalAlign="middle"
                                                                offsetX={4}
                                                                offsetY={6}
                                                            />
                                                        </React.Fragment>
                                                    );
                                                })}
                                        </Layer>
                                    </Stage>

                                    <Stack
                                        flex={1}
                                        sx={{
                                            p: '12px',
                                            background: 'rgba(0, 0, 0, 0.5)',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            textAlign: 'center',
                                            color: '#fff',
                                            zIndex: 1,
                                        }}
                                    >
                                        {e?.heading}
                                    </Stack>

                                    <Stack
                                        sx={{
                                            py: 1,
                                            px: 2,
                                            background: 'rgba(0, 0, 0, 0.5)',
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            textAlign: 'center',
                                            color: '#fff',
                                            zIndex: 10000000,
                                        }}
                                    >
                                        {formatDateByTimeZone(date, camera?.TimeZone ?? undefined, true)}
                                    </Stack>
                                </Box>
                            </Stack>

                            <PreviewImageWithGeoZone
                                open={openPreviewPhoto[e.previewKey]}
                                onClose={() => {}}
                                imgSrc={e.image}
                                scaleX={e.fullScale}
                                scaleY={e.fullScale}
                                geoZones={prevGeoZones}
                                cameraType={camera?.PTZCameraId !== null ? CameraType.PTZ : CameraType.Solar}
                            />
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </Stack>
    );
}
